import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, MessageSquare, CheckCheck, XCircle, Loader2,
  User, Building2, MapPin, Clock, Euro, Send, FileText, ExternalLink, Pencil
} from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../auth/AuthContext";
import { Card, Chip, Button, PageHeader, StatusDot, ReputationBadge } from "../components/Primitives";
import { getProposal, acceptProposal, rejectProposal, findChatForRequest, createOrGetChat } from "../lib/db";

const STATUS_STYLES = {
  sent:        "!bg-electric-500/10 !text-electric-600 !border-electric-500/30",
  pending:     "!bg-electric-500/10 !text-electric-600 !border-electric-500/30",
  viewed:      "!bg-carbon-700 !text-ink-800 !border-carbon-600",
  negotiating: "!bg-amber-500/10 !text-amber-500 !border-amber-500/30",
  accepted:    "!bg-status-available/10 !text-status-available !border-status-available/30",
  declined:    "!bg-red-500/10 !text-red-400 !border-red-500/30",
  withdrawn:   "!bg-carbon-700 !text-ink-500 !border-carbon-600",
  completed:   "!bg-electric-600/10 !text-electric-600 !border-electric-600/30",
};

export default function ProposalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'accept' | 'decline' | 'chat'

  useEffect(() => {
    let alive = true;
    getProposal(id)
      .then((row) => { if (alive) setP(row); })
      .catch((e) => { console.error(e); toast.error("Could not load proposal"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  if (loading) {
    return <div className="p-8 max-w-3xl mx-auto text-ink-500 font-mono text-xs"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>;
  }
  if (!p) {
    return (
      <div className="p-8 max-w-3xl mx-auto animate-fade-up">
        <button onClick={() => navigate(-1)} data-testid="proposal-back" className="text-ink-600 flex items-center gap-1 mb-4 hover:text-ink-900 text-sm">
          <ArrowLeft className="w-4 h-4" /> {t("common.back")}
        </button>
        <Card className="p-10 text-center"><p className="text-ink-700 text-sm">{t("proposalDetail.notFound")}</p></Card>
      </div>
    );
  }

  const isOwner = user?.id && p.request?.ownerId === user.id;
  const isApplicant = user?.id && p.applicant?.id === user.id;
  const canDecide = isOwner && (p.status === "sent" || p.status === "pending" || p.status === "viewed" || p.status === "negotiating");
  const canEdit = isApplicant && (p.status === "sent" || p.status === "pending" || p.status === "viewed" || p.status === "negotiating");

  const openChat = async () => {
    if (!user?.id || !p.request?.id) return;
    setBusy("chat");
    try {
      const otherId = isOwner ? p.applicant?.id : p.request.ownerId;
      let chatId = await findChatForRequest(user.id, p.request.id, otherId);
      if (!chatId && otherId) chatId = await createOrGetChat(user.id, otherId, p.request.id);
      if (chatId) navigate(`/app/messages?chat=${chatId}`); else navigate("/app/messages");
    } catch (e) { toast.error(e?.message || "Could not open chat"); }
    finally { setBusy(null); }
  };

  const doAccept = async () => {
    if (!canDecide || busy) return;
    setBusy("accept");
    try {
      const { chatId } = await acceptProposal(user.id, p.id);
      setP({ ...p, status: "accepted" });
      toast.success(t("proposalDetail.acceptedToast"));
      if (chatId) navigate(`/app/messages?chat=${chatId}`);
    } catch (e) { toast.error(e?.message || "Could not accept"); }
    finally { setBusy(null); }
  };

  const doDecline = async () => {
    if (!canDecide || busy) return;
    setBusy("decline");
    try {
      await rejectProposal(user.id, p.id);
      setP({ ...p, status: "declined" });
      toast.success(t("proposalDetail.declinedToast"));
    } catch (e) { toast.error(e?.message || "Could not decline"); }
    finally { setBusy(null); }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto animate-fade-up" data-testid="proposal-detail-page">
      <button onClick={() => navigate(-1)} data-testid="proposal-back" className="text-ink-600 flex items-center gap-1 mb-4 hover:text-ink-900 text-sm">
        <ArrowLeft className="w-4 h-4" /> {t("common.back")}
      </button>

      <PageHeader
        kicker={<>R4Pro · Proposal</>}
        title={p.request?.title || "Proposal"}
        testId="proposal-detail-header"
        right={<Chip className={`!text-[10px] ${STATUS_STYLES[p.status] || ""}`} data-testid="proposal-detail-status">{t(`myProposals.status.${p.status}`) || p.status?.toUpperCase()}</Chip>}
      />

      <Card className="p-4 mb-5" data-testid="proposal-detail-request-card">
        <div className="flex items-center gap-3 text-xs">
          <span className="font-mono text-electric-600">{p.request?.refCode}</span>
          <span className="text-ink-500">·</span>
          <span className="text-ink-800 truncate">{p.request?.title}</span>
          <button onClick={() => navigate(`/app/requests/${p.request?.refCode}`)} data-testid="open-request-from-proposal"
            className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-carbon-600 hover:border-electric-500 text-electric-600 text-[11px] shrink-0"
          >
            <ExternalLink className="w-3 h-3" /> {t("messages.openRequest")}
          </button>
        </div>
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] text-ink-600">
          <span className="font-mono">{p.request?.material || "—"}</span>
          <span className="font-mono">{p.request?.budget}</span>
          <span>{p.request?.deadline || "—"}</span>
          <span>{p.request?.location || "—"}</span>
        </div>
      </Card>

      <Card className="p-5 mb-5" data-testid="proposal-detail-producer-card">
        <div className="flex items-center gap-3">
          <button
            onClick={() => p.applicant?.id && navigate(`/app/producers/${p.applicant.id}`)}
            data-testid="proposal-detail-producer-link"
            className="w-11 h-11 rounded-xl bg-carbon-750 border border-carbon-600 flex items-center justify-center font-mono text-sm text-ink-800 shrink-0 hover:border-electric-500"
            aria-label={`Open profile of ${p.applicant?.name || "producer"}`}
          >
            {(p.applicant?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
          </button>
          <div className="flex-1 min-w-0">
            <button onClick={() => p.applicant?.id && navigate(`/app/producers/${p.applicant.id}`)}
              className="text-sm font-semibold text-ink-900 hover:text-electric-600 transition truncate flex items-center gap-1.5"
              data-testid="proposal-detail-producer-name"
            >
              {p.applicant?.name || "—"}
              {p.applicant?.accountType === "ORGANIZATION" ? <Building2 className="w-3 h-3 text-ink-500" /> : <User className="w-3 h-3 text-ink-500" />}
            </button>
            <div className="text-[11px] text-ink-500 flex items-center gap-2 flex-wrap mt-0.5">
              <StatusDot status={p.applicant?.availability || "available"} />
              <span>{t(`availability.${p.applicant?.availability || "available"}`)}</span>
              {p.applicant?.location && <><span>·</span><span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.applicant.location}</span></>}
              <ReputationBadge score={p.applicant?.reputation || 0} />
            </div>
            {p.applicant?.positioning && <p className="text-xs text-ink-700 mt-1.5 line-clamp-2">{p.applicant.positioning}</p>}
          </div>
        </div>
      </Card>

      <Card className="p-5 mb-5" data-testid="proposal-detail-body-card">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4 pb-4 border-b border-carbon-600">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-ink-600 mb-1 flex items-center gap-1"><Euro className="w-3 h-3" /> {t("proposal.priceLabel")}</div>
            <div className="text-sm text-ink-900 font-mono">
              {p.proposedPrice != null ? `€ ${Number(p.proposedPrice).toLocaleString("it-IT")}` : "—"}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-ink-600 mb-1 flex items-center gap-1"><Clock className="w-3 h-3" /> {t("proposal.daysLabel")}</div>
            <div className="text-sm text-ink-900 font-mono">{p.proposedDays != null ? `${p.proposedDays} ${t("common.days")}` : "—"}</div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-ink-600 mb-1">{t("proposalDetail.submitted")}</div>
            <div className="text-sm text-ink-900 font-mono">{p.when}</div>
          </div>
        </div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-ink-600 mb-2 flex items-center gap-1"><FileText className="w-3 h-3" /> {t("proposal.messageLabel")}</div>
        <p className="text-sm text-ink-800 leading-relaxed whitespace-pre-wrap" data-testid="proposal-detail-message">{p.excerpt || "—"}</p>
      </Card>

      <div className="flex flex-wrap gap-2 sticky bottom-4 md:static md:bottom-auto">
        {canDecide && (
          <>
            <Button onClick={doAccept} disabled={busy !== null} data-testid="proposal-accept" className="flex-1 h-11">
              {busy === "accept" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCheck className="w-4 h-4" /> {t("proposalDetail.accept")}</>}
            </Button>
            <Button variant="outline" onClick={doDecline} disabled={busy !== null} data-testid="proposal-decline" className="flex-1 h-11">
              {busy === "decline" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><XCircle className="w-4 h-4" /> {t("proposalDetail.decline")}</>}
            </Button>
          </>
        )}
        {canEdit && (
          <Button onClick={() => navigate(`/app/requests/${p.request?.refCode}/reply`)} data-testid="proposal-edit-cta" className="flex-1 h-11">
            <Pencil className="w-4 h-4" /> {t("proposalDetail.edit")}
          </Button>
        )}
        <Button variant="secondary" onClick={openChat} disabled={busy !== null} data-testid="proposal-open-chat" className="flex-1 h-11">
          {busy === "chat" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><MessageSquare className="w-4 h-4" /> {t("proposalDetail.chat")}</>}
        </Button>
      </div>
    </div>
  );
}
